function isEmergencyTenantText(text) {
  const value = String(text || '').toLowerCase();
  return (
    value.includes('emergency') ||
    value.includes('leak') ||
    value.includes('urgent')
  );
}

function isTenantChatMessage(msg) {
  if (!msg || msg._staffReply || msg.senderType === 'staff') return false;
  if (msg.senderType === 'tenant') return true;
  const fromUser = Number(msg.fromUserId);
  if (Number.isFinite(fromUser) && fromUser > 0) return false;
  return msg.fromPeopleId != null && msg.fromPeopleId !== '';
}

function isStaffChatMessage(msg) {
  if (!msg || isTenantChatMessage(msg)) return false;
  if (msg._staffReply || msg.senderType === 'staff') return true;
  const from = Number(msg.fromUserId);
  return Number.isFinite(from) && from > 0;
}

const INSIDE_CHAT_DND_ALWAYS_ON_KEY = 'insideChatDndAlwaysOn';
const INSIDE_CHAT_DND_DEVICE_KEY = 'insideChatDndDeviceId';

function isInsideChatDndActive(settings, now = new Date()) {
  if (!settings) return false;
  if (settings.alwaysOn === true) return true;
  if (!settings.until) return false;
  const until = new Date(settings.until);
  if (Number.isNaN(until.getTime())) return false;
  return now.getTime() < until.getTime();
}

function readDndAlwaysOn(storage, role) {
  let stored = null;
  try {
    stored = storage && storage.getItem(INSIDE_CHAT_DND_ALWAYS_ON_KEY);
  } catch (err) {
    stored = null;
  }
  if (stored === 'on') return true;
  if (stored === 'off') return false;
  return Number(role) >= 3;
}

function writeDndAlwaysOn(storage, on) {
  storage.setItem(INSIDE_CHAT_DND_ALWAYS_ON_KEY, on ? 'on' : 'off');
}

function applyStoredDndAlwaysOn(settings, role, storage) {
  return {
    ...(settings || {}),
    alwaysOn: readDndAlwaysOn(storage, role),
  };
}

function randomDndDeviceId() {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID().replace(/-/g, '');
  }
  return `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function readDndDeviceId(storage) {
  let stored = '';
  try {
    stored = storage && storage.getItem(INSIDE_CHAT_DND_DEVICE_KEY);
  } catch (err) {
    stored = '';
  }
  const existing = String(stored || '');
  if (/^[a-zA-Z0-9_-]{8,64}$/.test(existing)) return existing;
  const created = randomDndDeviceId().replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64);
  const deviceId = created.length >= 8 ? created : `device${Date.now().toString(36)}`;
  try {
    if (storage && typeof storage.setItem === 'function') {
      storage.setItem(INSIDE_CHAT_DND_DEVICE_KEY, deviceId);
    }
  } catch (err) {
    /* this page can still report the id for the current visit */
  }
  return deviceId;
}

function dndDeviceLabel(userAgent, deviceId) {
  const ua = String(userAgent || '');
  let kind = 'Browser';
  if (/iPhone/i.test(ua)) kind = 'iPhone';
  else if (/iPad/i.test(ua)) kind = 'iPad';
  else if (/Android/i.test(ua)) kind = 'Android';
  else if (/Windows/i.test(ua)) kind = 'Windows';
  else if (/Macintosh|Mac OS X/i.test(ua)) kind = 'Mac';
  else if (/Linux/i.test(ua)) kind = 'Linux';
  const tail = String(deviceId || '')
    .replace(/[^a-z0-9]/gi, '')
    .slice(-4)
    .toLowerCase();
  return tail ? `${kind} ${tail}` : kind;
}

function currentDndDevice(storage, userAgent) {
  const deviceId = readDndDeviceId(storage);
  return { deviceId, label: dndDeviceLabel(userAgent, deviceId) };
}

function shouldShowInsideChatAlert(msg, settings, now = new Date()) {
  if (!isInsideChatDndActive(settings, now)) return true;
  const exceptEmergencies = settings.exceptEmergencies !== false;
  const exceptStaff = settings.exceptStaff !== false;
  if (isTenantChatMessage(msg)) {
    if (messageHasKeyword(msg.message, settings.keywords)) return true;
    return exceptEmergencies && isEmergencyTenantText(msg.message);
  }
  if (isStaffChatMessage(msg)) return exceptStaff;
  return true;
}

function insideChatDndHowItWorks() {
  return [
    {
      title: '',
      body: 'Do not Disturb (DND) is for Inside Chat, SMS Messaging and Voice Messages alerts. It suppresses badges and phone alerts for all three channels, with exceptions that you can choose.',
    },
    {
      title: 'Per phone',
      body: 'Always On is saved on this phone only. The same person on two phones can leave it on here and off on the other. The phone that is off keeps its usual alerts.',
    },
    {
      title: 'Until',
      body: 'Do not disturb until is one time for the account. Both phones follow it. DND Status names the phone that last saved that time.',
    },
    {
      title: 'Exceptions',
      body: 'Except emergencies still alerts when a tenant note says emergency, leak, or urgent. Except staff still alerts for a message from another staff member. For SMS, that is a text that came in from a staff phone. A text Pixie sends out stays quiet. A keyword in a tenant message still alerts. Liked, Loved, and the other tapbacks do not alert.',
    },
    {
      title: 'Voice',
      body: 'Always On quiets Inside Chat and SMS on this phone. Voice Messages follow the until time, and stay on when only Always On is set.',
    },
  ];
}

function chatHeadAlertSample(chat) {
  if (!chat) return null;
  if (chat.groupId) {
    return { fromUserId: 1, toGroupId: chat.groupId, message: '' };
  }
  if (chat.personId != null && (chat.userId == null || chat.userId === '')) {
    return { fromPeopleId: chat.personId, message: '' };
  }
  if (Number(chat.userId) > 0) {
    return { fromUserId: Number(chat.userId), message: '' };
  }
  return { fromUserId: 0, message: '' };
}

const KEYWORD_FLAG_WINDOW_MS = 24 * 60 * 60 * 1000;

function parseKeywordList(text) {
  const seen = new Set();
  const words = [];
  const source = Array.isArray(text) ? text.join(',') : text;
  for (const part of String(source || '').split(',')) {
    const word = part.trim().toLowerCase();
    if (!word || seen.has(word)) continue;
    seen.add(word);
    words.push(word);
  }
  return words;
}

function messageHasKeyword(text, keywords) {
  const words = parseKeywordList(keywords);
  if (!words.length) return false;
  const value = String(text || '').toLowerCase();
  return words.some((word) => value.includes(word));
}

function keywordMessageStillOpen(messageId, resolvedMessageId) {
  const id = Number(String(messageId == null ? '' : messageId).replace(/^tenant_/, ''));
  const resolved = Number(resolvedMessageId);
  if (!Number.isFinite(id) || id <= 0) return false;
  if (!Number.isFinite(resolved) || resolved <= 0) return true;
  return id > resolved;
}

function keywordFlagIsCurrent(at, now = new Date()) {
  const time = new Date(at).getTime();
  if (Number.isNaN(time)) return false;
  const current = now.getTime();
  return time <= current && current - time < KEYWORD_FLAG_WINDOW_MS;
}

function chatHeadKeywordFlag(chat, flags, now = new Date()) {
  if (!chat || (chat.groupId != null && chat.groupId !== '')) return false;
  if (chat.personId == null || chat.personId === '') return false;
  const at =
    flags &&
    (flags[chat.personId] != null
      ? flags[chat.personId]
      : flags[Number(chat.personId)]);
  return keywordFlagIsCurrent(at, now);
}

/** Badge to draw while DND is on. The stored unreadCount is left intact. */
function displayedChatUnread(chat, settings, now = new Date()) {
  const unread = Number(chat && chat.unreadCount) || 0;
  if (unread <= 0) return 0;
  if (!isInsideChatDndActive(settings, now)) return unread;
  const sample = chatHeadAlertSample(chat);
  if (isTenantChatMessage(sample)) {
    const emergency =
      settings.exceptEmergencies === false ? 0 : Number(chat.emergencyUnread) || 0;
    const keyword = Number(chat.keywordUnread) || 0;
    return Math.min(unread, emergency + keyword);
  }
  if (!shouldShowInsideChatAlert(sample, settings, now)) return 0;
  return unread;
}

module.exports = {
  isEmergencyTenantText,
  isTenantChatMessage,
  isInsideChatDndActive,
  readDndAlwaysOn,
  writeDndAlwaysOn,
  applyStoredDndAlwaysOn,
  readDndDeviceId,
  dndDeviceLabel,
  currentDndDevice,
  shouldShowInsideChatAlert,
  displayedChatUnread,
  parseKeywordList,
  messageHasKeyword,
  keywordMessageStillOpen,
  keywordFlagIsCurrent,
  chatHeadKeywordFlag,
  insideChatDndHowItWorks,
};
